import { fetchDocumentThumbnails } from '@/utils/thumbnail-cache-utils';
import { isTempDocument } from '~/utils/document-utils';
import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import useConversations from '~/composables/useConversations';
import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';
import {
  AddToCollection,
  RemoveCollection,
  RemoveFromCollection,
  SetCollection,
  SetCollections,
  SetCollectionThumbs,
  UpdateCollection,
} from '~/constants/actions';
import { COLLECTIONS } from '~/constants/collections';
import { CURRENT_DIALOG } from '~/constants/conversationModels';
import { GetCollection, GetCollectionCount, GetOrganizationId, GetUserCollections } from '~/constants/getters';
import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { mergeDocumentData } from '~/utils/metadata';

export default () => {
  const store = useStore();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const { getDocumentThumbs, setSelectedDocument } = useDocuments();
  const { setConversationScope } = useConversations();
  const { setCurrentDocumentsDialog } = useModals();

  const activeOrganization = computed(() => store.getters[`authStore/${GetOrganizationId}`]);

  const favoriteDocuments = computed(
    () => store.getters[`collectionStore/${GetCollection}`](COLLECTIONS.favorites).documents,
  );
  const favoriteDocumentsCount = computed(() =>
    store.getters[`collectionStore/${GetCollectionCount}`](COLLECTIONS.favorites),
  );

  const allCollections = computed(() => store.getters[`collectionStore/${GetUserCollections}`]);

  const currentCollections = computed(() =>
    allCollections.value.filter((col) => !col.collectionId.includes(COLLECTIONS.favorites)),
  );

  const openModalInCollectionScope = async (collectionId, collectionName, document) => {
    // if document is defined we have the last level of breadcrumb
    const currentCollection = currentCollections.value.find((current) => current.collectionId === collectionId);
    const name = collectionName || currentCollection.collectionName;
    const breadcrumb = [{ title: ` ${name} ` }, ...(document ? [{ title: ` ${document.name} ` }] : [])];
    if (document) {
      await setSelectedDocument({ reference: document });
    }
    setConversationScope({ isScoped: true, collection: currentCollection, breadcrumb });
    setCurrentDocumentsDialog(CURRENT_DIALOG.collections_panel);
  };

  const getCollection = (collectionId) =>
    computed(() => {
      const collection = store.getters[`collectionStore/${GetCollection}`](collectionId);
      return collection;
    });

  const isInCollection = (collectionId, externalId) =>
    computed(() => {
      const index = store.getters[`collectionStore/${GetCollection}`](collectionId).documents.findIndex((fav) => {
        const id = fav.externalId || fav;
        return id === externalId;
      });
      return index > -1;
    });

  const setCollectionThumbs = async (collectionId, thumbs) => {
    await store.dispatch(`collectionStore/${SetCollectionThumbs}`, {
      collectionId,
      thumbs,
    });
  };

  const setThumbsCollectionsView = async (collections, totalThumbs) => {
    const collectionsThumbs = await fetchThumbnailsForAllCollections(collections, totalThumbs);
    collectionsThumbs.forEach((col) => {
      setCollectionThumbs(col.id, col.thumbs);
    });
  };

  const getCollectionThumb = async (collection, index) => {
    let thumb = collection.thumbUrl;
    if (index === collection.documents.length - 1 && collection.documents.length > 1) {
      const id =
        collection.documents[collection.documents.length - 2].externalId ||
        collection.documents[collection.documents.length - 2];
      const response = await getDocumentThumbs({ documents: [{ id, pages: [1] }] });
      thumb = response[0].pages[1].small;
    }
    // treat the special case when we remove the last document from the collection and should have the default thumb
    if (collection.documents.length === 1) {
      thumb = '';
    }
    return thumb;
  };

  const handleCollections = async (document, collectionId) => {
    const { externalId } = document;
    const collection = allCollections.value.find((col) => col.collectionId === collectionId);
    const index = collection.documents.findIndex((doc) => {
      const id = doc.externalId || doc;
      return id === externalId;
    });
    const modifiedAt = Date.now();

    if (index > -1) {
      // treat the special case when the removed document is the last one in our list and we have to retrieve the thumb of the previous
      // for collectionThumb
      const thumb = await getCollectionThumb(collection, index);
      await store.dispatch(`collectionStore/${RemoveFromCollection}`, {
        collectionId,
        documentId: externalId,
        modifiedAt,
        ...(thumb ? { thumbUrl: thumb } : {}),
      });
      await arcaneApiGateway.patch(apiGatewayEndpoints.collections.removeDocumentFromCollection, {
        indexToRemove: index,
        collectionId:
          collectionId === COLLECTIONS.favorites
            ? `${COLLECTIONS.favorites}/${activeOrganization.value}`
            : collectionId,
      });
    } else {
      await store.dispatch(`collectionStore/${AddToCollection}`, {
        collectionId,
        document,
        modifiedAt,
        thumbUrl: document.pages ? document.pages[1].small : '',
      });
      await arcaneApiGateway.patch(apiGatewayEndpoints.collections.addDocumentToCollection, {
        documentId: externalId,
        collectionId:
          collectionId === COLLECTIONS.favorites
            ? `${COLLECTIONS.favorites}/${activeOrganization.value}`
            : collectionId,
      });
    }
  };

  const getUserCollections = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.collections.userCollections);
      await store.dispatch(`collectionStore/${SetCollections}`, await pruneMissingDocuments(data));
    } catch (error) {
      console.log('Failed to load user collections', error);
    }
  };

  const fetchAdditionalDocumentsData = async (collection) => {
    const { documents = [] } = collection || {};
    if (documents.length) {
      const externalIds = documents
        .map((fav) => (typeof fav === 'string' ? fav : fav?.externalId || null))
        .filter((id) => id && !isTempDocument(id));

      try {
        const metadataFavorites = await arcaneApiGateway.post(apiGatewayEndpoints.documents.metadata, {
          externalIds,
        });

        const documents = metadataFavorites.data.map(({ externalId }) => {
          return { id: externalId, pages: [1] };
        });

        const fetchFromApi = async (uncachedDocuments) => {
          const response = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.pageCovers, {
            documents: uncachedDocuments,
          });
          return response.data;
        };

        const documentsThumbs = await fetchDocumentThumbnails(documents, fetchFromApi);

        // need to merge the three-piece of data for the documents, preserving the order of favorites
        // need to preserve the order because deletion of favorites is based on index
        const updatedDocuments = mergeDocumentData(metadataFavorites.data, documentsThumbs, externalIds);
        return updatedDocuments;
      } catch (error) {
        console.log('Error fetching additional data', error);
      }
    }
    return [];
  };

  const findMissingDocumentIds = async (documents = []) => {
    const externalIds = [
      ...new Set(documents.map((doc) => (typeof doc === 'string' ? doc : doc?.externalId || null)).filter(Boolean)),
    ];
    if (!externalIds.length) return [];

    try {
      const { data: missingIds } = await arcaneApiGateway.post(apiGatewayEndpoints.documents.missingMetadata, {
        externalIds,
      });
      return missingIds;
    } catch (error) {
      console.log('Error checking missing documents', error);
      return [];
    }
  };

  const dropMissingDocuments = (documents, missingIdsSet) =>
    documents.filter((doc) => {
      const id = typeof doc === 'string' ? doc : doc?.externalId;
      return !id || !missingIdsSet.has(id);
    });

  const filterMissingDocumentsFromCollection = async (collection) => {
    if (!collection?.documents?.length) return collection;
    const missingIds = await findMissingDocumentIds(collection.documents);
    if (!missingIds.length) return collection;
    return { ...collection, documents: dropMissingDocuments(collection.documents, new Set(missingIds)) };
  };

  const pruneMissingDocuments = async (collections) => {
    const collectionsWithDocuments = collections.filter((col) => col.documents?.length);
    if (!collectionsWithDocuments.length) return collections;

    const missingIds = await findMissingDocumentIds(collectionsWithDocuments.flatMap((col) => col.documents));
    if (!missingIds.length) return collections;
    const missingIdsSet = new Set(missingIds);

    return collections.map((col) =>
      col.documents?.length ? { ...col, documents: dropMissingDocuments(col.documents, missingIdsSet) } : col,
    );
  };

  const setAdditionalDocumentsData = async (collectionId) => {
    const collection = getCollection(collectionId).value;
    const updatedDocuments = await fetchAdditionalDocumentsData(collection);
    const updatedCollection = { ...collection, documents: updatedDocuments };
    await store.dispatch(`collectionStore/${SetCollection}`, {
      collectionId: collectionId,
      ...collection,
      ...updatedCollection,
    });
  };

  const reorderFavorites = async (favoriteDocuments) => {
    const documents = favoriteDocuments.map((doc) => doc.externalId);
    await arcaneApiGateway.patch(apiGatewayEndpoints.collections.reorderDocuments, {
      documents,
    });

    await store.dispatch(`collectionStore/${SetCollection}`, {
      collectionId: COLLECTIONS.favorites,
      documents: favoriteDocuments,
    });
  };

  const deleteAllDocuments = async () => {
    const documents = [];
    await arcaneApiGateway.patch(apiGatewayEndpoints.collections.reorderDocuments, {
      documents,
    });

    await store.dispatch(`collectionStore/${SetCollection}`, {
      collectionId: COLLECTIONS.favorites,
      documents,
    });
  };

  const updateCollection = async ({ collectionId, collectionName, collectionDescription }) => {
    const {
      data: { response },
    } = await arcaneApiGateway.patch(apiGatewayEndpoints.collections.updateCollection, {
      collectionId,
      collectionName,
      collectionDescription,
    });
    await store.dispatch(`collectionStore/${UpdateCollection}`, response);
  };

  const createCollection = async ({ collectionName, collectionDescription }) => {
    try {
      const {
        data: { collection },
      } = await arcaneApiGateway.post(apiGatewayEndpoints.collections.addCollection, {
        collectionName,
        collectionDescription,
      });
      await store.dispatch(`collectionStore/${SetCollection}`, collection);
    } catch (error) {
      console.log('Error creating a new collection', error);
    }
  };

  const removeCollection = async (collectionId) => {
    try {
      // for delete method, we need explicitly data IN BODY
      await arcaneApiGateway.delete(apiGatewayEndpoints.collections.removeCollection, {
        data: { collectionId },
      });
      await store.dispatch(`collectionStore/${RemoveCollection}`, collectionId);
    } catch (error) {
      console.log('Error removing a  collection', error);
    }
  };

  async function fetchThumbnailsForAllCollections(collections, totalThumbs) {
    // Map each item to a promise that resolves to the item with its thumbnails
    // need the last 5 thumbnails
    const fetchPromises = collections.map(async (col) => {
      const lastFiveDocumentsIDs = col.documents.slice(-totalThumbs).map((document) => {
        return { id: document.externalId || document, pages: [1] };
      });
      const collectionThumbnails = await getDocumentThumbs({ documents: lastFiveDocumentsIDs });
      return { id: col.collectionId, thumbs: collectionThumbnails }; // Return the col with its thumbnails added
    });

    // Resolve all promises and return the full list of collections with thumbnails
    return Promise.all(fetchPromises);
  }

  // thumb of the collection is the thumb of last document from the collection inn AddToCollectionModal
  const setCollectionsThumbs = async (collections) => {
    const collectionsThumbs = await fetchThumbnailsForAllCollections(collections, 1);

    const updatedCollections = collections.map((col) => {
      const thumbUrl = collectionsThumbs.find((thumb) => thumb.id === col.collectionId).thumbs[0]?.pages[1].small || '';
      return { ...col, thumbUrl };
    });
    await store.dispatch(`collectionStore/${SetCollections}`, updatedCollections);
  };

  const getSharedCollectionLink = async (collectionId) => {
    try {
      const {
        data: { id },
      } = await arcaneApiGateway.post(apiGatewayEndpoints.collections.createCollectionSharedLink, {
        collectionId,
      });
      return id ? id : '';
    } catch (err) {
      console.log('Error in creating shared link', err);
    }
  };
  const validateSharedLink = async (sharedLinkId) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.collections.validateSharedLink(sharedLinkId));
      return data;
    } catch (err) {
      console.log('Error in validating shared link', err);
    }
  };
  const createSharedCollection = async (sharedLinkId, parentId) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.collections.createSharedCollection, {
        sharedLinkId,
        parentId,
      });
      if (data.isValid && data.collection) {
        await store.dispatch(`collectionStore/${SetCollection}`, data.collection);
        openModalInCollectionScope(data.collection.collectionId, data.collection.collectionName);
      }
      return data;
    } catch (err) {
      console.log('Error in creating a shared collection', err);
    }
  };

  const isReadOnly = (collection) => !!(collection && collection.parentId);

  return {
    createCollection,
    createSharedCollection,
    currentCollections,
    allCollections,
    deleteAllDocuments,
    favoriteDocuments,
    favoriteDocumentsCount,
    filterMissingDocumentsFromCollection,
    getCollection,
    getSharedCollectionLink,
    isReadOnly,
    setAdditionalDocumentsData,
    setCollectionThumbs,
    setThumbsCollectionsView,
    fetchAdditionalDocumentsData,
    setCollectionsThumbs,
    isInCollection,
    handleCollections,
    getUserCollections,
    openModalInCollectionScope,
    reorderFavorites,
    removeCollection,
    updateCollection,
    validateSharedLink,
  };
};
